import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BackButtonToolbar from "features/BackButtonToolbar";
import dataProvider from "server/dataProvider";
import {
  DateField,
  FunctionField,
  Labeled,
  RecordContextProvider,
  ReferenceField,
  SelectField,
  SimpleShowLayout,
  TextField,
  Title,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { specialistRequests } from "api/specialistApi";
import { useNotifyError } from "utils/useNotifyError";
import UserField from "server/components/Fields/UserField";
import { HealthcareApplicationOrigin } from "utils/constants";
import { formatDateTime } from "utils/helpers";
import { HealthcareApplicationOriginChoices } from "utils/choices";
import { SectionTitle } from "features/SectionTitle";
const ShowHealthcareCampaignApplication = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const notifyError = useNotifyError();

  // Both ids come from the URL, not router state: state is lost on reload and on the redirect back
  // after re-login, while the query string is preserved.
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("campaignId");
  const applicationId = searchParams.get("applicationId");

  const [details, setDetails] = useState(null);

  const fetchDetails = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        specialistRequests.getHealthcareCampaignApplicationById(campaignId, applicationId),
      );
      setDetails(data);
    } catch (err) {
      notifyError(err);
    }
  }, [campaignId, applicationId, notifyError]);

  useEffect(() => {
    if (!campaignId || !applicationId) return;

    fetchDetails();
  }, [campaignId, applicationId, fetchDetails]);

  return (
    <RecordContextProvider value={details}>
      <Title title="Детайли на кандидатура за здравни грижи" />
      <BackButtonToolbar />
      <SectionTitle
        title="Лична информация"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <SimpleShowLayout>
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Имена">
              <FunctionField
                label="Имена"
                render={rec =>
                  rec?.secondName
                    ? `${rec?.firstName} ${rec?.secondName} ${rec?.lastName}`
                    : `${rec?.firstName} ${rec?.lastName}`
                }
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="ЕГН/ЛНЧ">
              <TextField source="personId" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="УИН">
              <TextField source="uin" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Дата на раждане">
              <DateField
                source="birthDate"
                locales="bg-BG"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Имейл">
              <TextField source="email" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Телефон">
              <TextField source="contactPhone" />
            </Labeled>
          </Grid>
        </Grid>

        <SectionTitle
          title="Информация за кандидатурата"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />

        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Курс здравна грижа">
              <TextField source="courseNameBg" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Желана начална дата">
              <DateField
                source="desiredDate"
                locales="bg-BG"
                emptyText="Няма посочена"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Специалност на кандидата">
              <ReferenceField
                source="participantSpeciality"
                reference={specialistRequests.getHealthcareSpecialitySelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Клиника">
              <TextField source="clinic" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Статус">
              <TextField source="statusNameBg" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Дата на подаване">
              <FunctionField render={rec => formatDateTime(rec?.createdOn)} />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Произход">
              <SelectField
                source="origin"
                choices={HealthcareApplicationOriginChoices}
              />
            </Labeled>
          </Grid>
          {details?.origin === HealthcareApplicationOrigin.Admin && (
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Създадена от">
                <UserField source="createdByUserId" />
              </Labeled>
            </Grid>
          )}
        </Grid>
      </SimpleShowLayout>
    </RecordContextProvider>
  );
};

export default ShowHealthcareCampaignApplication;
